import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import {
  buildResponseValidationReport,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import {
  type ActivationAskResponse,
  type SupplierActivationPayload,
} from '../types';
import { zodActivationAskResponseSchema } from '../validations';

/**
 * Submits a supplier activation request for the given data exchange.
 *
 * Returns the batch activation status envelope, which includes one entry per
 * resolved supplier contact email (single-contact mode returns one entry).
 *
 * Validates strictly: `onSuccess` dereferences `data.batches` immediately to
 * list notified contacts.
 *
 * @param deId - The data exchange ID of the supplier to activate.
 * @param payload - The activation request payload.
 * @param options - Optional ky request options (e.g., abort signal).
 * @returns The created activation batch(es).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/post_supplier_activation_suppliers__de_id__activation_post
 */
export const postSupplierActivation = async (
  deId: UUID,
  payload: SupplierActivationPayload,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.SUPPLIER.POST_ACTIVATION(deId), {
      ...options,
      json: payload,
    })
    .json();

  const parsed = zodActivationAskResponseSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'post-supplier-activation',
      message: 'post supplier activation response has validation errors',
    });

    throw new Error(
      'Post supplier activation response did not match the expected shape',
    );
  }

  return parsed.data satisfies ActivationAskResponse;
};
